import { useEffect, useState } from 'react';
import { CheckOutlined, CopyOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import { useTaoKhoa } from '../hooks/useThamDinh';
import type { KhoaMoiTao } from '../types';

export interface HopTaoKhoaProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Tạo khoá API.
 *
 * Hai giai đoạn trong một hộp: nhập tên và webhook → hiện khoá vừa sinh. Giai đoạn hai **không đóng được
 * bằng Esc hay bấm ra ngoài** theo phản xạ, vì backend chỉ lưu băm của khoá (đặc tả mục 13.6) — đóng hộp là
 * mất khoá vĩnh viễn, phải thu hồi và tạo lại.
 */
export function HopTaoKhoa({ open, onClose }: HopTaoKhoaProps) {
  const { t } = useT();
  const dichLoi = useLoi();
  const tao = useTaoKhoa();

  const [ten, setTen] = useState('');
  const [webhook, setWebhook] = useState('');
  const [moi, setMoi] = useState<KhoaMoiTao | undefined>();
  const [daChep, setDaChep] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    setTen('');
    setWebhook('');
    setMoi(undefined);
    setDaChep(undefined);
    tao.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function chep(gia: string, khoa: string) {
    try {
      await navigator.clipboard.writeText(gia);
      setDaChep(khoa);
    } catch {
      // Trình duyệt chặn clipboard (không phải HTTPS, hoặc người dùng từ chối): ô vẫn chọn tay được.
      setDaChep(undefined);
    }
  }

  const loiTruong = loiTheoTruong(tao.error);
  const loiChung = tao.error && Object.keys(loiTruong).length === 0 ? dichLoi(tao.error) : undefined;

  if (moi) {
    return (
      <Dialog open={open} onClose={onClose} title={t('thamDinh.khoa.daTaoTieuDe')}>
        <div className="space-y-4 pt-2">
          <div className="flex gap-2 rounded-lg border border-warning-200 bg-warning-50 p-3">
            <WarningOutlined className="mt-0.5 shrink-0 text-warning-600" />
            <p className="text-sm text-warning-800">{t('thamDinh.khoa.chiHienMotLan')}</p>
          </div>

          <OBiMat
            nhan={t('thamDinh.khoa.khoaApi')}
            giaTri={moi.khoa}
            daChep={daChep === 'khoa'}
            onChep={() => void chep(moi.khoa, 'khoa')}
          />

          {moi.bi_mat_webhook && (
            <OBiMat
              nhan={t('thamDinh.khoa.biMatWebhook')}
              mo={t('thamDinh.khoa.biMatWebhookMo')}
              giaTri={moi.bi_mat_webhook}
              daChep={daChep === 'bi-mat'}
              onChep={() => void chep(moi.bi_mat_webhook, 'bi-mat')}
            />
          )}

          <div className="flex justify-end">
            <Button variant="primary" size="md" onClick={onClose}>
              {t('thamDinh.khoa.daChepXong')}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title={t('thamDinh.khoa.taoTieuDe')}>
      <div className="space-y-4 pt-2">
        {loiChung && (
          <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{loiChung}</p>
        )}

        <Field label={t('thamDinh.khoa.ten')} required hint={t('thamDinh.khoa.tenMo')} error={loiTruong['/ten']}>
          {(p) => <Input {...p} value={ten} onChange={(e) => setTen(e.target.value)} />}
        </Field>

        <Field
          label={t('thamDinh.khoa.webhookUrl')}
          hint={t('thamDinh.khoa.webhookUrlMo')}
          error={loiTruong['/webhook_url']}
        >
          {(p) => (
            <Input {...p} placeholder="https://" value={webhook} onChange={(e) => setWebhook(e.target.value)} />
          )}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={tao.isPending}
            disabled={!ten.trim()}
            onClick={() =>
              tao.mutate(
                { ten: ten.trim(), webhook_url: webhook.trim() || undefined },
                { onSuccess: setMoi },
              )
            }
          >
            {t('thamDinh.khoa.taoXacNhan')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** Ô chỉ đọc mang một bí mật, kèm nút chép. */
function OBiMat({ nhan, mo, giaTri, daChep, onChep }: {
  nhan: string; mo?: string; giaTri: string; daChep: boolean; onChep: () => void;
}) {
  const { t } = useT();
  return (
    <Field label={nhan} hint={mo}>
      {(p) => (
        <div className="flex gap-2">
          <Input {...p} readOnly value={giaTri} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
          <Button
            variant="outline"
            size="md"
            icon={daChep ? <CheckOutlined /> : <CopyOutlined />}
            onClick={onChep}
          >
            {daChep ? t('thamDinh.khoa.daChep') : t('thamDinh.khoa.sao')}
          </Button>
        </div>
      )}
    </Field>
  );
}
